import { Link } from 'react-router-dom'
import { SkeletonStack } from '@/shared/ui'
import type { ChangeEvent } from '@/shared/api/types'
import { useClientEvents } from '../model'
import { ClientEventRow } from './ClientEventRow'
import { useMarkEventsReadOnView } from './useMarkEventsReadOnView'
import styles from './ClientEventsWidget.module.css'

const EMPTY_EVENTS: ChangeEvent[] = []

interface ClientEventsWidgetProps {
  limit?: number
}

/** Короткая лента для обзора; полная страница использует те же строки. */
export function ClientEventsWidget({ limit = 4 }: ClientEventsWidgetProps) {
  const events = useClientEvents({ limit })
  const items = events.data?.items ?? EMPTY_EVENTS

  useMarkEventsReadOnView(items, events.data?.unread ?? 0)

  return (
    <section className={styles.panel} aria-labelledby="client-events-widget-title">
      <div className={styles.head}>
        <span className={styles.headIcon} aria-hidden="true">
          <PulseIcon />
        </span>
        <div className={styles.headText}>
          <h2 id="client-events-widget-title" className={styles.title}>
            Что нового по клиентам
          </h2>
          <span className={styles.subtitle}>Факты и договорённости после разговоров</span>
        </div>
        {Boolean(events.data?.unread) && (
          <span className={styles.unread}>{formatUnread(events.data?.unread ?? 0)}</span>
        )}
        <Link className={styles.allLink} to="/client-events">
          Вся лента
          <ArrowIcon />
        </Link>
      </div>

      {events.isPending ? (
        <div className={styles.loading} aria-label="Загружаем изменения по клиентам">
          <SkeletonStack rows={3} height={60} />
        </div>
      ) : events.isError ? (
        <div className={styles.state} role="alert">
          <span className={styles.stateTitle}>Лента временно недоступна</span>
          <span className={styles.stateHint}>{errorMessage(events.error)}</span>
          <button type="button" className={styles.retry} onClick={() => void events.refetch()}>
            Попробовать снова
          </button>
        </div>
      ) : items.length > 0 ? (
        <div className={styles.list}>
          {items.map((event) => (
            <ClientEventRow key={event.id} event={event} compact />
          ))}
        </div>
      ) : (
        <div className={styles.state}>
          <span className={styles.emptyIcon} aria-hidden="true">
            <EmptyIcon />
          </span>
          <span className={styles.stateTitle}>Изменений пока нет</span>
          <span className={styles.stateHint}>
            После разобранных звонков здесь появятся новые факты о клиентах.
          </span>
        </div>
      )}
    </section>
  )
}

function formatUnread(unread: number): string {
  return unread > 99 ? '99+ новых' : `${unread} новых`
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Не удалось получить события'
}

function PulseIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <path d="M1.8 8H4.5L6.1 4.3L8.5 11.7L10.2 7.2H14.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function ArrowIcon() {
  return (
    <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
      <path d="M3 6.5H10M7.5 4L10 6.5L7.5 9" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function EmptyIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
      <path d="M4 13H8L10.2 7.8L13.6 18.2L16.1 11.8H22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="13" cy="13" r="10" stroke="currentColor" strokeWidth="1.3" opacity="0.5" />
    </svg>
  )
}
